import { Dialog } from '@components/dialog';
import { IconButton } from '@components/iconButton';
import { Tooltip } from '@components/tooltip';
import { List, ListItem, Typography } from '@mui/material';
import { Grid, Box } from '@mui/system';
import { useStore } from '@store/GlobalStore';
import { getTodaysHoliday, holidayConfig, HolidayMode } from '@theme/palettes/getPalette';
import { useApiEnvStore } from '@utils/api-client';
import { useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
/** @jsxImportSource @emotion/react */
import { keyframes } from '@emotion/react';
import { useEasterEggStore } from '@components/holiday/useEasterEggStore';
// Halloween keyframes using Emotion
const flicker = keyframes`
  0% { box-shadow: 0 0 32px 8px orange, 0 0 0 100vmax rgba(0,0,0,0.5); }
  100% { box-shadow: 0 0 108px 26px #ff9800, 0 0 0 100vmax rgba(0,0,0,0.6); }
`;
const pulse = keyframes`
  0% { box-shadow: 0 0 8px 2px #ff9800; }
  100% { box-shadow: 0 0 4px 8px #ff9800; }
`;
// Easter keyframes
const easterGlow = keyframes`
  0% { box-shadow: 0 0 12px 4px #C3AED6, 0 0 0 100vmax rgba(240, 235, 244, 0.3); }
  100% { box-shadow: 0 0 24px 8px #FFB7C5, 0 0 0 100vmax rgba(240, 235, 244, 0.4); }
`;
import { shallow } from 'zustand/shallow';

import SpookySound from '@assets/holiday/halloween/scarry-sound2.mp3';

const getHolidayIcon = (holiday: HolidayMode) => {
  if (holiday === HolidayMode.Halloween) return '👻';
  if (holiday === HolidayMode.Easter) return '🐰';
  return '🎉';
};

export const HolidayModeControl = () => {
  const [disableHolidayModeFlag, toggleDisableHolidayModeFlag] = useApiEnvStore(
    (state) => [state.disableHolidayModeFlag, state.toggleDisableHolidayModeFlag],
    shallow,
  );

  const [showConfirmHolidayMode, setShowConfirmHolidayMode] = useState(false);
  const eggCount = useEasterEggStore((state) => state.getCurrentYearCount());

  const holiday = getTodaysHoliday();
  const [userAuth, currentUser] = useStore((state) => [state.userAuth, state.currentUser], shallow);

  const handleToggleHolidayMode = () => {
    if (disableHolidayModeFlag) {
      datadogLogs.logger.info('Holiday mode enabled', {
        holiday,
        userId: currentUser?.id,
        sessionId: window.sessionStorage.getItem('lio-session-id'),
      });
    }
    setShowConfirmHolidayMode(false);
    toggleDisableHolidayModeFlag();
    window.location.reload();
  };

  const isInternalUser = userAuth.isInternalUser;

  if (!holiday || !isInternalUser) {
    return null;
  }

  const holidayDetails = holidayConfig[holiday];

  const title = disableHolidayModeFlag ? `Celebrate ${holidayDetails?.title}!` : `Disable ${holidayDetails?.title}`;

  const isEaster = holiday === HolidayMode.Easter;
  const isHalloween = holiday === HolidayMode.Halloween;

  const getDialogSx = (): Record<string, any> => {
    if (isHalloween) {
      return {
        px: 3,
        py: 2,
        boxShadow: '0 0 32px 8px orange, 0 0 0 100vmax rgba(0,0,0,0.5)',
        border: '2px solid orange',
        background: 'linear-gradient(135deg, #181818 80%, #ff9800 100%)',
        animation: `${flicker} 2s infinite alternate`,
        position: 'relative',
        '& .MuiButton-root': {
          background: '#181818',
          color: '#ff9800',
          border: '2px solid #ff9800',
          boxShadow: '0 0 8px 2px #ff9800',
          animation: `${pulse} 1.2s infinite alternate`,
        },
        span: { color: 'white !important' },
      };
    }
    if (isEaster) {
      return {
        px: 3,
        py: 2,
        border: '2px solid #C3AED6',
        background: 'linear-gradient(135deg, #F0EBF4 60%, #D4F1E8 80%, #FFE8F0 100%)',
        animation: `${easterGlow} 2.5s infinite alternate`,
        position: 'relative',
        '& .MuiButton-root': {
          background: '#F0EBF4',
          color: '#5B3A6B',
          border: '2px solid #7BC8A4',
          boxShadow: '0 0 6px 2px #C3AED6',
        },
      };
    }
    return { px: 3, py: 2 };
  };

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          bottom: 14,
          right: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {isEaster && !disableHolidayModeFlag && (
          <Box
            sx={{
              background: 'linear-gradient(135deg, #C3AED6, #7BC8A4)',
              borderRadius: '16px',
              px: 1.5,
              py: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              boxShadow: '0 2px 8px rgba(91, 58, 107, 0.2)',
            }}
          >
            <span style={{ fontSize: 16 }}>🥚</span>
            <Typography variant='bodySmall' sx={{ color: '#fff', fontWeight: 'bold', fontSize: 13 }}>
              {eggCount}
            </Typography>
          </Box>
        )}
        <Tooltip title={title} placement='top'>
          <IconButton
            id='holiday-mode-button'
            onClick={() => {
              if (disableHolidayModeFlag) {
                if (isHalloween) {
                  const audio = new Audio(SpookySound);
                  audio.volume = 0.7;
                  audio.play();
                }
                setShowConfirmHolidayMode(true);
              } else {
                handleToggleHolidayMode();
              }
            }}
          >
            <span role='img' aria-label='holiday' style={{ fontSize: 40 }}>
              {getHolidayIcon(holiday)}
            </span>
          </IconButton>
        </Tooltip>
      </Box>
      {showConfirmHolidayMode && (
        <Dialog
          showBorders={false}
          type='confirmation'
          handleCloseDialog={() => setShowConfirmHolidayMode(false)}
          primaryBtnText={isEaster ? 'Start the Hunt!' : 'Continue'}
          primaryBtnAction={handleToggleHolidayMode}
          open={true}
          PaperProps={{
            className: isHalloween ? 'halloween-modal' : isEaster ? 'easter-modal' : undefined,
            sx: getDialogSx(),
          }}
          slotProps={{}}
        >
          <Grid sx={{ textAlign: 'center', mb: 2 }}>
            <Typography
              variant='body'
              fontWeight='bold'
              sx={
                isHalloween ? { color: '#ff9800', textShadow: '0 0 8px #ff9800' } : isEaster ? { color: '#5B3A6B' } : {}
              }
            >
              {isHalloween && '🎃 '}
              {isEaster && '🐰 '}
              Let&apos;s celebrate {holidayDetails?.title}!{isHalloween && ' 👻'}
              {isEaster && ' 🐰'}
            </Typography>

            {isEaster ? (
              <Box sx={{ textAlign: 'left', mt: 1.5 }}>
                <Typography variant='bodySmall' sx={{ ml: 1, color: '#5B3A6B' }}>
                  Welcome to the LIO Easter Egg Hunt!
                </Typography>
                <List sx={{ listStyleType: 'number', padding: '5px 0 10px 20px' }}>
                  <ListItem sx={sxStyles.listItem}>
                    <Typography variant='bodySmall' sx={{ color: '#5B3A6B' }}>
                      Colorful Easter eggs are hidden throughout the portal. Click them to collect!
                    </Typography>
                  </ListItem>
                  <ListItem sx={sxStyles.listItem}>
                    <Typography variant='bodySmall' sx={{ color: '#5B3A6B' }}>
                      Your egg count is tracked and saved. Can you find them all?
                    </Typography>
                  </ListItem>
                  <ListItem sx={sxStyles.listItem}>
                    <Typography variant='bodySmall' sx={{ color: '#5B3A6B' }}>
                      This is only visible to internal users. Brokers won&apos;t see it.
                    </Typography>
                  </ListItem>
                  <ListItem sx={sxStyles.listItem}>
                    <Typography variant='bodySmall' sx={{ color: '#5B3A6B' }}>
                      Click the egg emoji again to turn it off.
                    </Typography>
                  </ListItem>
                </List>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'left', mt: 1 }}>
                <Typography variant='bodySmall' sx={{ ml: 1 }}>
                  A few things to note:
                </Typography>
                <List sx={{ listStyleType: 'number', padding: '5px 0 10px 20px' }}>
                  <ListItem sx={sxStyles.listItem}>
                    <Typography variant='bodySmall'>
                      This easter egg is only available to internal users. Brokers will not see it.
                    </Typography>
                  </ListItem>
                  <ListItem sx={sxStyles.listItem}>
                    <Typography variant='bodySmall'>To turn it off, just click the emoji again.</Typography>
                  </ListItem>
                </List>
              </Box>
            )}
          </Grid>
        </Dialog>
      )}
    </>
  );
};

const sxStyles = {
  listItem: {
    display: 'list-item',
    padding: '0',
    fontSize: 14,
    fontWeight: 100,
  },
};
